import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import '@fontsource/inter/100.css';
import '@fontsource/inter/200.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import React from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation
} from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import AuthProvider from './context/AuthProvider';
import Accounts from './screens/accounts';
import CreatePassword from './screens/authentication/CreatePassword';
import CreateProfile from './screens/authentication/CreateProfile';
import ForgotPassword from './screens/authentication/ForgotPassword';
import SignIn from './screens/authentication/Signin';
import Cases from './screens/Cases';
import Case from './screens/Cases/Case';
import Counsellors from './screens/counsellors';
import EmergencyResponders from './screens/emergencyResponders';
import Dashboard from './screens/dashboard/Dashboard';
import Resources from './screens/resources';
import Sessions from './screens/sessions';
import appTheme from './theme';

import './App.css';
import Analytics from './screens/Analytics';
import FolderView from './screens/resources/FolderView';
import DashboardProvider from './context/DashboardProvider';
import VerifyResetCode from './screens/authentication/VerifyResetCode';
import VerifyCommunityInvite from './screens/resources/VerifyCommunityInvite';
import RoleProtected from './components/RoleProtected';
import { Permission } from './constants/roles';

const theme = extendTheme({
  ...appTheme
});

const Protected = ({ children }: { children: JSX.Element }) => {
  const auth = React.useContext(AuthContext);
  const location = useLocation();

  if (!auth?.authenticated) {
    return (
      <Navigate
        to='/authentication/signin'
        state={{ from: location }}
        replace
      />
    );
  }

  return children;
};

function App() {
  return (
    <ChakraProvider theme={theme}>
      <AuthProvider>
        <DashboardProvider>
          <BrowserRouter>
            <Routes>
              <Route path='/authentication/signin' element={<SignIn />} />
              <Route
                path='/authentication/create-profile'
                element={<CreateProfile />}
              />
              <Route
                path='/authentication/create-password'
                element={<CreatePassword />}
              />
              <Route
                path='/authentication/forgot-password'
                element={<ForgotPassword />}
              />
              <Route
                path='/authentication/verify-reset-code'
                element={<VerifyResetCode />}
              />
              <Route
                path='/dashboard'
                element={
                  <Protected>
                    <Dashboard />
                  </Protected>
                }
              />
              <Route
                path='/analytics'
                element={
                  <Protected>
                    <Analytics />
                  </Protected>
                }
              />
              <Route
                path='/cases'
                element={
                  <RoleProtected permission={Permission.VIEW_CASES}>
                    <Cases />
                  </RoleProtected>
                }
              />
              <Route
                path='/cases/:id'
                element={
                  <RoleProtected permission={Permission.VIEW_CASES}>
                    <Case />
                  </RoleProtected>
                }
              />
              <Route
                path='/sessions'
                element={
                  <RoleProtected permission={Permission.VIEW_SESSIONS}>
                    <Sessions />
                  </RoleProtected>
                }
              />
              <Route
                path='/counsellors'
                element={
                  <RoleProtected permission={Permission.VIEW_COUNSELLORS}>
                    <Counsellors />
                  </RoleProtected>
                }
              />
              <Route
                path='/emergency-responders'
                element={
                  <RoleProtected permission={Permission.VIEW_COUNSELLORS}>
                    <EmergencyResponders />
                  </RoleProtected>
                }
              />
              <Route
                path='/accounts'
                element={
                  <Protected>
                    <Accounts />
                  </Protected>
                }
              />
              <Route
                path='/resources'
                element={
                  <RoleProtected permission={Permission.MANAGE_RESOURCES}>
                    <Resources />
                  </RoleProtected>
                }
              />
              <Route
                path='/community/verify-invite'
                element={
                  <RoleProtected permission={Permission.MANAGE_RESOURCES}>
                    <VerifyCommunityInvite />
                  </RoleProtected>
                }
              />
              <Route
                path='/resources/:id'
                element={
                  <RoleProtected permission={Permission.MANAGE_RESOURCES}>
                    <FolderView />
                  </RoleProtected>
                }
              />
              <Route path='*' element={<Navigate to='/dashboard' />} />
            </Routes>
          </BrowserRouter>
        </DashboardProvider>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
